import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import { listStoreInstagramVideos } from "../../Redux/Actions/InstagramVideoActions";

import {
  Swiper,
  SwiperSlide,
} from "swiper/react";

import { Autoplay } from "swiper/modules";

import "swiper/css";

import "../styles/instagram-videos.css";

const InstagramVideosCarousel = () => {
  const dispatch = useDispatch();

  const swiperRef = useRef(null);
  const videoRefs = useRef({});

  const [playingId, setPlayingId] =
    useState(null);

  const instagramVideoState = useSelector(
    (state) =>
      state.instagramVideoStoreList
  );

  const {
    loading,
    error,
    videos: rawVideos,
  } = instagramVideoState || {};

  const videos = useMemo(() => {
    return Array.isArray(rawVideos)
      ? rawVideos.slice(0, 12)
      : [];
  }, [rawVideos]);

  useEffect(() => {
    if (!videos.length) {
      dispatch(
        listStoreInstagramVideos()
      );
    }
  }, [dispatch, videos.length]);

  const getPlayableVideoUrl = (url = "") => {
    if (!url) return "";

    const isCloudinaryVideo =
      url.includes("res.cloudinary.com") &&
      url.includes("/video/upload/");

    const isMov =
      /\.mov(\?.*)?$/i.test(url);

    if (
      isCloudinaryVideo &&
      isMov
    ) {
      return url
        .replace(
          "/video/upload/",
          "/video/upload/f_mp4,q_auto/"
        )
        .replace(
          /\.mov(\?.*)?$/i,
          ".mp4"
        );
    }

    return url;
  };

  const pauseAllVideos = (
    exceptId = null
  ) => {
    Object.entries(
      videoRefs.current
    ).forEach(([id, element]) => {
      if (
        id !== exceptId &&
        element &&
        !element.paused
      ) {
        element.pause();
        element.currentTime = 0;
      }
    });
  };

  const toggleVideo = async (
    videoId
  ) => {
    const selectedVideo =
      videoRefs.current[videoId];

    if (!selectedVideo) return;

    pauseAllVideos(videoId);

    if (selectedVideo.paused) {
      try {
        swiperRef.current
          ?.autoplay
          ?.stop();

        selectedVideo.muted = false;
        selectedVideo.volume = 1;

        await selectedVideo.play();

        setPlayingId(videoId);
      } catch (playError) {
        console.error(
          "No se pudo reproducir el video:",
          playError
        );
      }
    } else {
      selectedVideo.pause();
      selectedVideo.currentTime = 0;

      setPlayingId(null);

      swiperRef.current
        ?.autoplay
        ?.start();
    }
  };

  const handleSlideChange = () => {
    pauseAllVideos();

    setPlayingId(null);

    swiperRef.current
      ?.autoplay
      ?.start();
  };

  const handleVideoEnded = (
    videoId
  ) => {
    const currentVideo =
      videoRefs.current[videoId];

    if (currentVideo) {
      currentVideo.currentTime = 0;
    }

    setPlayingId(null);

    swiperRef.current
      ?.autoplay
      ?.start();
  };

  if (
    loading &&
    !videos.length
  ) {
    return (
      <section className="looks-video-section">
        <div className="site-shell">
          <div className="site-shell-inner">
            <p className="looks-video-status">
              Cargando Novedades...
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (
    error ||
    !videos.length
  ) {
    return null;
  }

  return (
    <section className="looks-video-section">
      <div className="site-shell">
        <div className="site-shell-inner">
          <div className="looks-video-header">
            <div>
              <h2>
                Novedades Alta Moda
              </h2>

              <p>
                Mantente al día con nuestras novedades, promociones, tendencias y mucho más.
              </p>
            </div>

            <a
              href="https://www.instagram.com/altamoda17.valledupar/"
              target="_blank"
              rel="noopener noreferrer"
              className="looks-instagram-button"
            >
              <i
                className="fab fa-instagram"
                aria-hidden="true"
              />

              <span>
                Ver más en Instagram
              </span>

            </a>
          </div>

          <div className="looks-video-swiper-wrap">
            <Swiper
              modules={[Autoplay]}
              onSwiper={(swiper) => {
                swiperRef.current =
                  swiper;
              }}
              loop={videos.length > 1}
              autoplay={
                videos.length > 1
                  ? {
                      delay: 3500,
                      disableOnInteraction:
                        false,
                      pauseOnMouseEnter:
                        true,
                    }
                  : false
              }
              grabCursor
              watchOverflow
              speed={650}
              spaceBetween={18}
              onSlideChange={
                handleSlideChange
              }
              breakpoints={{
                0: {
                  slidesPerView: 1,
                  spaceBetween: 14,
                },

                480: {
                  slidesPerView: 2,
                  spaceBetween: 14,
                },

                768: {
                  slidesPerView: 3,
                  spaceBetween: 16,
                },

                1100: {
                  slidesPerView: 4,
                  spaceBetween: 18,
                },

                1400: {
                  slidesPerView: 6,
                  spaceBetween: 18,
                },
              }}
            >
              {videos.map((video) => {
                const isPlaying =
                  playingId ===
                  video._id;

                return (
                  <SwiperSlide
                    key={video._id}
                  >
                    <article className="looks-video-card">
                      <button
                        type="button"
                        className="looks-video-media"
                        onClick={() =>
                          toggleVideo(
                            video._id
                          )
                        }
                        aria-label={
                          isPlaying
                            ? `Pausar ${video.title}`
                            : `Reproducir ${video.title}`
                        }
                      >
                        <video
                          ref={(element) => {
                            if (element) {
                              videoRefs.current[
                                video._id
                              ] = element;
                            } else {
                              delete videoRefs
                                .current[
                                video._id
                              ];
                            }
                          }}
                          src={getPlayableVideoUrl(
                            video.videoUrl
                          )}
                          playsInline
                          preload="metadata"
                          onPlay={() =>
                            setPlayingId(
                              video._id
                            )
                          }
                          onPause={() =>
                            setPlayingId(
                              (
                                currentId
                              ) =>
                                currentId ===
                                video._id
                                  ? null
                                  : currentId
                            )
                          }
                          onEnded={() =>
                            handleVideoEnded(
                              video._id
                            )
                          }
                        />

                        {!isPlaying && (
                          <span
                            className="looks-play-button"
                            aria-hidden="true"
                          >
                            ▶
                          </span>
                        )}

                        {isPlaying && (
                          <span
                            className="looks-pause-indicator"
                            aria-hidden="true"
                          >
                            ❚❚
                          </span>
                        )}
                      </button>

                      <h3>
                        {video.title}
                      </h3>
                    </article>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InstagramVideosCarousel;